import React, { useEffect, useState } from "react"
import { Axis, CrmScoringUser, SortBy, SortOrder } from "../../actions/crmScoring"
import { DataTable, DataTableColumn, DataTableSortStatus } from "mantine-datatable"
import { F2_LABELS, getSegmentZone, M2_LABELS, R2_LABELS } from "./personas"
import { ActionIcon, Badge, Box, Button, Group, Text, TextInput, Title, Tooltip } from "@mantine/core"
import { CaretRightIcon, DownloadSimpleIcon } from "@phosphor-icons/react"
import UserDetailModal from "./UserDetailModal"
import { useDisclosure } from "@mantine/hooks"
import { TablePagination } from "../TablePagination"

interface Props {
  clientId: number
  axis: Axis
  users: CrmScoringUser[]
  total: number
  page: number
  perPage: number
  loading: boolean
  searchQuery: string
  onSearchQueryChange: (q: string) => void
  checkedUserIds: number[]
  onSetCheckedUsers: (ids: number[]) => void
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: number) => void
  sort: { sortBy: SortBy; sortOrder: SortOrder }
  onSortChange: (sortBy: SortBy, sortOrder: SortOrder) => void
  snapshotDate: string
  onCsvDownload: () => void
  csvLoading: boolean
}

const SegmentUserList: React.FC<Props> = ({
  clientId,
  axis,
  users,
  total,
  page,
  perPage,
  loading,
  searchQuery,
  onSearchQueryChange,
  checkedUserIds,
  onSetCheckedUsers,
  onPageChange,
  onPerPageChange,
  sort,
  onSortChange,
  snapshotDate,
  onCsvDownload,
  csvLoading
}) => {
  // 閉じるときは opened だけ false にし、フェード中も氏名・IDを残す
  const [detailUser, setDetailUser] = useState<CrmScoringUser | null>(null)
  const [detailOpened, detailModal] = useDisclosure(false)

  // 検索はサーバー側（BigQuery）で実行。連打を避けるため、検索ボタンクリックまたは Enter キー押下で初めて適用する
  const [localSearch, setLocalSearch] = useState(searchQuery)
  useEffect(() => { setLocalSearch(searchQuery) }, [searchQuery])
  const searchDirty = localSearch !== searchQuery
  const applySearch = () => {
    if (!searchDirty) return
    onSearchQueryChange(localSearch)
  }
  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') applySearch()
  }
  const clearSearch = () => {
    setLocalSearch('')
    if (searchQuery !== '') onSearchQueryChange('')
  }

  const isF2M2 = axis === 'f2_m2'
  const totalPages = Math.max(1, Math.ceil(total / perPage))

  // DataTable は現ページ分のレコードしか返さないため、他ページの選択を保持したままマージする
  const selectedRecords = users.filter((u) => checkedUserIds.includes(u.simple_user_id))
  const handleSelectedRecordsChange = (records: CrmScoringUser[]) => {
    const pageIds = users.map((u) => u.simple_user_id)
    onSetCheckedUsers([
      ...checkedUserIds.filter((id) => !pageIds.includes(id)),
      ...records.map((r) => r.simple_user_id),
    ])
  }

  const segmentColumn: DataTableColumn<CrmScoringUser> = {
    accessor: 'segment',
    title: 'セグメント',
    sortable: false,
    render: (u) => {
      const seg = getSegmentZone(u.f2, u.m2)
      return (
        <Badge
          variant="light"
          style={{
            backgroundColor: seg.bgClass,
            color: seg.textClass,
            border: `1px solid ${seg.borderClass}`,
          }}
        >
          {seg.name}
        </Badge>
      )
    },
  }

  // accessor は API の sort_by 値と一致させる（segment / detail はソート対象外）
  const columns: DataTableColumn<CrmScoringUser>[] = [
    {
      accessor: 'name',
      title: '紹介者名',
      sortable: true,
      render: (u) => (
        <>
          <Text fw={600}>{u.name || '-'}</Text>
          <Text fz={11} c="#00000099">ID: {u.simple_user_id}</Text>
        </>
      ),
    },
    { accessor: 'code', title: 'コード', sortable: true, render: (u) => u.code || '-' },
    { accessor: 'email', title: 'メール', sortable: true, render: (u) => u.email || '-' },
    { accessor: 'phone', title: '電話番号', sortable: true, render: (u) => u.phone || '-' },
    {
      accessor: 'from_max_date',
      title: '最終紹介日',
      sortable: true,
      render: (u) => u.from_max_date || '-',
    },
    {
      accessor: 'from_count',
      title: <>紹介行動<Text span fz={11} c="#00000099">（F2）</Text></>,
      sortable: true,
      render: (u) => (
        <>
          <Text>{u.from_count} 回</Text>
          <Text fz={11} c="#00000099">F2: {F2_LABELS[u.f2]}</Text>
        </>
      ),
    },
    {
      accessor: isF2M2 ? 'm2' : 'r2',
      title: (
        <>
          {isF2M2 ? 'ゲストCV' : '最終紹介'}
          <Text span fz={11} c="#00000099">（{isF2M2 ? 'M2' : 'R2'}）</Text>
        </>
      ),
      sortable: true,
      render: (u) => (isF2M2 ? (
        <>
          <Text>{M2_LABELS[u.m2]}</Text>
          <Text fz={11} c="#00000099">M2ランク: {u.m2}</Text>
        </>
      ) : (
        <>
          <Text>{u.from_max_date}</Text>
          <Text fz={11} c="#00000099">R2: {R2_LABELS[u.r2]}</Text>
        </>
      )),
    },
    ...(isF2M2 ? [segmentColumn] : []),
    {
      accessor: 'detail',
      title: '詳細',
      sortable: false,
      textAlign: 'center',
      render: (u) => (
        <Tooltip label="紹介者詳細を表示">
          <ActionIcon
            variant="subtle"
            onClick={() => {
              setDetailUser(u)
              detailModal.open()
            }}
          >
            <CaretRightIcon size={18} />
          </ActionIcon>
        </Tooltip>
      ),
    },
  ]

  // 行数が少ないときにテーブル下部の余白が出ないよう、内部スクロールが必要な件数のときだけ
  // 高さを固定する。0件時は「データがありません」の表示領域を確保する。
  // （maxHeight は内部の ScrollArea が flex アイテムで縮まないため機能しない）
  const tableHeight = users.length === 0 ? 400 : users.length >= 15 ? 600 : undefined

  return (
    <Box>
      <Group justify="space-between" align="center" gap={16}>
        <Group gap={8} align="center">
          <Title order={2} size={18} fw={600}>対象ユーザーリスト</Title>
          <Badge variant="light">{total} 件</Badge>
        </Group>

        <Group gap={12} align="center">
          <TextInput
            placeholder="名前、コード、メール、電話、IDで検索..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.currentTarget.value)}
            onKeyDown={onSearchKeyDown}
            w={350}
          />
          <Button
            variant="filled"
            radius="xl"
            fz={13}
            fw={600}
            onClick={applySearch}
            disabled={!searchDirty}
          >
            検索
          </Button>
          {searchQuery && (
            <Button
              variant="subtle"
              radius="xl"
              fz={13}
              onClick={clearSearch}
              title="検索をクリア"
            >
              クリア
            </Button>
          )}
          <Button
            variant="outline"
            radius="xl"
            onClick={onCsvDownload}
            disabled={total === 0 || csvLoading}
            leftSection={<DownloadSimpleIcon size={16} weight="bold" />}
          >
            {csvLoading ? 'ダウンロード中' : 'CSVダウンロード'}
          </Button>
        </Group>
      </Group>

      {/* メール/LINE配信は後日実装のため、選択時アクションバーは非表示 */}

      <Box
        mt={16}
        style={{
          border: '1px solid #0000001A',
          borderRadius: 8,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <DataTable
          withColumnBorders
          records={users}
          idAccessor="simple_user_id"
          columns={columns}
          height={tableHeight}
          fetching={loading}
          noRecordsText="該当するユーザーが見つかりません。"
          styles={{
            root: { border: 'none' },
            header: { backgroundColor: '#F8F9FA' },
          }}
          selectedRecords={selectedRecords}
          onSelectedRecordsChange={handleSelectedRecordsChange}
          sortStatus={{ columnAccessor: sort.sortBy, direction: sort.sortOrder }}
          onSortStatusChange={(s: DataTableSortStatus<CrmScoringUser>) =>
            onSortChange(s.columnAccessor as SortBy, s.direction)
          }
        />

        <TablePagination
          page={page}
          onChangePage={onPageChange}
          perPage={perPage}
          onChangePerPage={onPerPageChange}
          totalPages={totalPages}
          isEmpty={users.length === 0}
        />
      </Box>

      {detailUser && (
        <UserDetailModal
          opened={detailOpened}
          clientId={clientId}
          simpleUserId={detailUser?.simple_user_id ?? null}
          userName={detailUser?.name ?? ''}
          snapshotDate={snapshotDate}
          onClose={detailModal.close}
        />
      )}
    </Box>
  )
}

export default SegmentUserList
